import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import AISearchInput from './components/AISearchInput';
import AISuggestions from './components/AISuggestions';
import AIIdleSuggestions from './components/AIIdleSuggestions';
import AIThinking from './components/AIThinking';
import AIResponseView from './components/AIResponseView';
import { AITypingState, AISearchResult } from './types';
import { cleanQuery, matchesQuery } from './helpers';
import { searchShipmentHistoryAI } from './smartSearchService';
import { STORAGE_KEY_RECENT_SEARCHES, MAX_RECENT_SEARCHES, DEFAULT_SUGGESTIONS } from './constants';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface AISmartSearchProps {
  onResults: (filters: AISearchResult['filters'] | null, activeResult: AISearchResult | null) => void;
  isOpen: boolean;
  onClose: () => void;
}

export default function AISmartSearch({ onResults, isOpen, onClose }: AISmartSearchProps) {
  const [query, setQuery] = useState('');
  const [typingState, setTypingState] = useState<AITypingState>('idle');
  const [result, setResult] = useState<AISearchResult | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastExecutedQueryRef = useRef('');
  const requestIdRef = useRef(0);

  // Load recent & suggested searches from local constants/storage whenever the panel is opened
  useEffect(() => {
    if (isOpen) {
      setSuggestions(DEFAULT_SUGGESTIONS);
      try {
        const stored = localStorage.getItem(STORAGE_KEY_RECENT_SEARCHES);
        if (stored) {
          setRecentSearches(JSON.parse(stored));
        } else {
          setRecentSearches([]);
        }
      } catch (e) {
        console.error('Failed to load recent searches from localStorage', e);
        setRecentSearches([]);
      }
    }
  }, [isOpen]);

  // Primary search executor
  const executeSearch = useCallback(
    (q: string) => {
      const cleaned = cleanQuery(q);

      // Cancel any pending timers
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Ignore queries shorter than 3 chars or whitespace-only
      if (!cleaned || cleaned.length < 3) {
        setTypingState('idle');
        setResult(null);
        onResults(null, null);
        lastExecutedQueryRef.current = '';
        return;
      }

      // Prevent duplicate API calls if query is exactly the same as last executed
      if (matchesQuery(cleaned, lastExecutedQueryRef.current)) {
        if (result) {
          setTypingState(result.matchedRecords.length > 0 ? 'success' : 'empty');
        }
        return;
      }

      // Setup race condition protection
      const reqId = ++requestIdRef.current;
      setTypingState('thinking');
      lastExecutedQueryRef.current = cleaned;

      searchShipmentHistoryAI({ query: cleaned, conversationId })
        .then(res => {
          // Ignore stale responses
          if (reqId !== requestIdRef.current) return;

          setResult(res);
          setTypingState(res.matchedRecords.length > 0 ? 'success' : 'empty');
          setConversationId(res.conversationId);

          // Save the search query to local storage search history
          setRecentSearches(prev => {
            const cleanedQuery = cleaned.trim();
            const filtered = prev.filter(q => q.toLowerCase() !== cleanedQuery.toLowerCase());
            const updated = [cleanedQuery, ...filtered].slice(0, MAX_RECENT_SEARCHES);
            try {
              localStorage.setItem(STORAGE_KEY_RECENT_SEARCHES, JSON.stringify(updated));
            } catch (e) {
              console.error('Failed to save recent search to localStorage', e);
            }
            return updated;
          });

          // Propagate results upwards
          onResults(res.filters, res);
        })
        .catch(err => {
          if (reqId !== requestIdRef.current) return;
          console.error('AI search failed', err);
          setTypingState('error');
          onResults(null, null);
        });
    },
    [conversationId, onResults, result],
  );

  // Handle change in search input
  const handleQueryChange = (val: string) => {
    setQuery(val);

    const cleaned = cleanQuery(val);
    if (!cleaned) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      setTypingState('idle');
      setResult(null);
      onResults(null, null);
      lastExecutedQueryRef.current = '';
      setConversationId(undefined); // Clear conversation context
      return;
    }

    setTypingState('typing');

    // Debounce: Cancel previous timer and start a new 600ms timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setTypingState('debouncing');
      executeSearch(val);
    }, 600);
  };

  // Skip debounce and execute immediately
  const handleForceSearch = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    executeSearch(query);
  };

  // Reset/Clear search
  const handleClear = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setQuery('');
    setTypingState('idle');
    setResult(null);
    lastExecutedQueryRef.current = '';
    setConversationId(undefined);
    onResults(null, null);
  }, [onResults]);

  // Refine query (conversational memory)
  const handleRefine = (term: string) => {
    if (term === 'Export Results') {
      alert('Simulating shipment history report export...');
      return;
    }
    // Append refinement term to active query text
    const newQuery = `${query} ${term}`;
    setQuery(newQuery);
    executeSearch(newQuery);
  };

  const handleSelectSuggestion = (val: string) => {
    setQuery(val);
    setIsFocused(false);
    executeSearch(val);
  };

  // Handle deleting recent search locally from local storage
  const handleDeleteRecent = (searchToDelete: string) => {
    setRecentSearches(prev => {
      const updated = prev.filter(q => q !== searchToDelete);
      try {
        localStorage.setItem(STORAGE_KEY_RECENT_SEARCHES, JSON.stringify(updated));
      } catch (e) {
        console.error('Failed to delete recent search from localStorage', e);
      }
      return updated;
    });
  };

  const hasNoInput = !query.trim();

  // If the panel is closed, render nothing but keep component mounted to preserve search state
  if (!isOpen) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          borderRadius: '24px',
          borderColor: '#e2e8f0',
          backgroundColor: '#ffffff',
          boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.02)',
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        {/* Header section with Close button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: '#7c3aed', fontSize: '20px' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#4c1d95', fontSize: '18px' }}>
              AI Smart Search
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={onClose}
            sx={{
              color: '#64748b',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '13px',
              px: 2,
              py: 0.5,
              borderRadius: '20px',
              backgroundColor: '#f1f5f9',
              '&:hover': {
                color: '#334155',
                backgroundColor: '#e2e8f0',
              },
            }}
          >
            Minimize Workspace
          </Button>
        </Box>

        {/* Input container with relative positioning for absolute dropdown */}
        <Box sx={{ position: 'relative', width: '100%', mb: 3 }}>
          <AISearchInput
            value={query}
            typingState={typingState}
            onChange={handleQueryChange}
            onClear={handleClear}
            onForceSearch={handleForceSearch}
            autoFocus={isOpen}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />

          {/* Frontend-Managed Idle Dropdown (only when focused, empty, and not loading) */}
          {isFocused && hasNoInput && typingState !== 'thinking' && typingState !== 'debouncing' && (
            <AIIdleSuggestions onSelect={handleSelectSuggestion} />
          )}
        </Box>

        {/* Thinking state loader */}
        {typingState === 'thinking' && <AIThinking />}

        {/* Results details panel */}
        {typingState === 'success' && result && (
          <AIResponseView result={result} onClickFollowUp={handleRefine} onClear={handleClear} />
        )}

        {/* Dynamic suggested & recent searches from Backend (rendered only when input is empty and NOT focused) */}
        {!isFocused && hasNoInput && (
          <AISuggestions
            onSelect={handleSelectSuggestion}
            onDeleteRecent={handleDeleteRecent}
            recentSearches={recentSearches}
            suggestions={suggestions}
          />
        )}

        {/* Smart Empty State */}
        {typingState === 'empty' && result && (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              borderRadius: '16px',
              borderColor: '#e2e8f0',
              backgroundColor: '#f8fafc',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <SentimentDissatisfiedIcon sx={{ fontSize: 48, color: '#94a3b8' }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155', mb: 0.5 }}>
                I couldn't find shipments matching:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#64748b', fontWeight: 600 }}>
                "{result.query}"
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: '#64748b', maxWidth: '400px', mb: 1 }}>
              Try refining your keywords or choose from these recommended searches below:
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, maxWidth: '500px' }}>
              {result.suggestions.map(suggestion => (
                <Button
                  key={suggestion}
                  variant="outlined"
                  size="small"
                  onClick={() => handleSelectSuggestion(suggestion)}
                  sx={{
                    borderRadius: '16px',
                    borderColor: '#cbd5e1',
                    color: '#475569',
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      borderColor: '#7c3aed',
                      color: '#7c3aed',
                      backgroundColor: '#faf5ff',
                    },
                  }}
                >
                  {suggestion}
                </Button>
              ))}
            </Box>
          </Paper>
        )}

        {/* Error state */}
        {typingState === 'error' && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: '16px',
              borderColor: '#fca5a5',
              backgroundColor: '#fef2f2',
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle1" sx={{ color: '#dc2626', fontWeight: 700, mb: 1 }}>
              Something went wrong while processing your request
            </Typography>
            <Button variant="outlined" color="error" size="small" onClick={handleForceSearch}>
              Try Again
            </Button>
          </Paper>
        )}
      </Paper>
    </Box>
  );
}
